import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { PurchaseService } from "../../../services";
import { ActionButton, FormDialog, InputField, SelectField } from "../../v3";
import { materialValidationRules } from "../../../pages/library/materials/validation";

export const AddEditMaterial = ({ open, onClose, materialId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [unitSelectOptions, setUnitSelectOptions] = useState([]);
  const [materialDetails, setMaterialDetails] = useState({
    name: undefined,
    description: undefined,
    taxPercentage: undefined,
    unitOfMeasure: undefined,
    defaultPrice: undefined,
  });

  useEffect(() => {
    if (open && materialId) {
      fetchData(materialId);
    } else {
      handleClearForm();
    }
    setErrors({});
  }, [open, materialId]);

  useEffect(() => {
    if (open) fetchUnits();
  }, [open]);

  const fetchUnits = async () => {
    try {
      const { data } = await PurchaseService.getUnitOfMeasurements();
      const rows = Array.isArray(data) ? data : data?.rows || [];
      setUnitSelectOptions(rows.map((item) => ({ label: item.name, value: item.name })));
    } catch (error) {
      console.error("Failed to fetch units", error);
    }
  };

  const fetchData = async (materialId) => {
    try {
      const materialResponse = await PurchaseService.getPurchaseItemById(materialId);
      setMaterialDetails({
        name: materialResponse?.data?.name,
        description: materialResponse?.data?.description,
        taxPercentage: Number(materialResponse?.data?.taxPercentage),
        unitOfMeasure: materialResponse?.data?.unitOfMeasure,
        defaultPrice: Number(materialResponse?.data?.defaultPrice),
      });
    } catch (error) {
      toast.error("Failed to fetch Material details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleClearForm = () => {
    setMaterialDetails({
      name: undefined,
      description: undefined,
      taxPercentage: undefined,
      unitOfMeasure: undefined,
      defaultPrice: undefined,
    });
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setMaterialDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const taxSlabs = [
    { label: "0%", value: 0 },
    { label: "6%", value: 6 },
    { label: "12%", value: 12 },
    { label: "18%", value: 18 },
  ];

  const handleSaveMaterial = async () => {
    setErrors({});
    const errors = materialValidationRules(materialDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    try {
      setLoading(true);
      if (materialId) {
        await PurchaseService.updatePurchaseItem(materialId, {
          ...materialDetails,
          defaultPrice: Number(materialDetails.defaultPrice),
        });
        onSuccess?.();
      } else {
        const response = await PurchaseService.createPurchaseItem({
          ...materialDetails,
          defaultPrice: Number(materialDetails.defaultPrice),
        });
        onSuccess?.(response?.data);
      }
      toast.success(`Material ${materialId ? "updated" : "added"} successfully`);
    } catch (error) {
      console.error("🚀 ~ handleSaveMaterial ~ error:", error);
      toast.error("Failed to save Material");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  return (
    <FormDialog
      open={open}
      title={materialId ? "Edit Material" : "Add Material"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveMaterial} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box className="fl gap-2">
          <InputField
            required
            label="Material Name"
            name="name"
            value={materialDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            style={{ width: "100%" }}
          />
        </Box>
        <Box className="fl gap-2 w-100">
          <InputField
            label="Description"
            name="description"
            value={materialDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            style={{ width: "100%" }}
          />
          <InputField
            label="Price"
            name="defaultPrice"
            value={materialDetails.defaultPrice}
            onChange={updateDetailsState}
            error={!!errors.defaultPrice}
            helperText={errors.defaultPrice}
            style={{ width: "100%" }}
          />
        </Box>
        <Box className="fl gap-2 w-100">
          <InputField
            select
            required
            label="Unit"
            name="unitOfMeasure"
            value={materialDetails.unitOfMeasure || ""}
            selectOptions={unitSelectOptions}
            onChange={updateDetailsState}
            error={!!errors.unitOfMeasure}
            helperText={errors.unitOfMeasure}
            style={{ width: "100%" }}
          />
          <InputField
            select={true}
            selectOptions={taxSlabs}
            label="Tax %"
            name="taxPercentage"
            value={materialDetails.taxPercentage}
            onChange={updateDetailsState}
            error={!!errors.taxPercentage}
            helperText={errors.taxPercentage}
            style={{ width: "100%" }}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
